import { useTranslation } from 'react-i18next';

interface ComparisonResult {
    compatibilityPercentage: number;
    differences: Array<{
        actual: any;
        details: any;
        expected: any;
        message: string;
        rule: string;
        ruleType: string;
        section: string;
        subsection: string;
    }>;
    passedChecks: number;
    totalChecks: number;
    sectionResults: Array<{
        differences: any[];
        passedChecks: number;
        section: string;
        totalChecks: number;
        subsectionResults: Array<{
            differences: any[];
            passedChecks: number;
            subsection: string;
            totalChecks: number;
        }>;
    }>;
}

interface ComparisonOverviewProps {
    comparisonResult: ComparisonResult;
}

const ComparisonOverview = ({ comparisonResult }: ComparisonOverviewProps) => {
    const [t] = useTranslation();

    const getCompatibilityColor = (percentage: number) => {
        if (percentage >= 80) return 'text-success-600 dark:text-success-400';
        if (percentage >= 60) return 'text-warning-600 dark:text-warning-400';
        return 'text-error-600 dark:text-error-400';
    };

    const getCompatibilityBgColor = (percentage: number) => {
        if (percentage >= 80) return 'bg-success-100 dark:bg-success-900/30';
        if (percentage >= 60) return 'bg-warning-100 dark:bg-warning-900/30';
        return 'bg-error-100 dark:bg-error-900/30';
    };

    const getCompatibilityBorderColor = (percentage: number) => {
        if (percentage >= 80) return 'border-success-200 dark:border-success-800';
        if (percentage >= 60) return 'border-warning-200 dark:border-warning-800';
        return 'border-error-200 dark:border-error-800';
    };

    const getCompatibilityIcon = (percentage: number) => {
        if (percentage >= 80) return 'icon-[ph--check-circle]';
        if (percentage >= 60) return 'icon-[ph--warning-circle]';
        return 'icon-[ph--x-circle]';
    };

    const getCompatibilityLabel = (percentage: number) => {
        if (percentage >= 80) return t('Pages:ScoresheetPage.HighCompatibility', 'High Compatibility');
        if (percentage >= 60) return t('Pages:ScoresheetPage.ModerateCompatibility', 'Moderate Compatibility');
        return t('Pages:ScoresheetPage.LowCompatibility', 'Low Compatibility');
    };

    return (
        <div className="bg-white dark:bg-content-800 rounded-lg border border-content-200 dark:border-content-700 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-content-200 dark:border-content-700 bg-primary-50/50 dark:bg-primary-900/20">
                <h2 className="font-header text-xl font-semibold text-primary-900 dark:text-primary-100">
                    {t('Pages:ScoresheetPage.StyleCompatibilityAnalysis', 'Style Compatibility Analysis')}
                </h2>
            </div>

            <div className="p-6 font-sans">
                {/* Main Compatibility Score */}
                <div className={`p-6 rounded-lg border ${getCompatibilityBgColor(comparisonResult.compatibilityPercentage)} ${getCompatibilityBorderColor(comparisonResult.compatibilityPercentage)} mb-6`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`${getCompatibilityIcon(comparisonResult.compatibilityPercentage)} h-8 w-8 ${getCompatibilityColor(comparisonResult.compatibilityPercentage)}`} />
                            <div>
                                <div className="text-2xl font-bold text-content-900 dark:text-content-100">
                                    {comparisonResult.compatibilityPercentage.toFixed(1)}%
                                </div>
                                <div className={`text-sm font-medium ${getCompatibilityColor(comparisonResult.compatibilityPercentage)}`}>
                                    {getCompatibilityLabel(comparisonResult.compatibilityPercentage)}
                                </div>
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-sm text-content-600 dark:text-content-400">
                                {t('Pages:ScoresheetPage.OverallMatch', 'Overall Style Match')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <div className="icon-[ph--check-circle] h-6 w-6 text-success-600 dark:text-success-400" />
                            <div>
                                <div className="text-2xl font-bold text-success-800 dark:text-success-200">
                                    {comparisonResult.passedChecks}
                                </div>
                                <div className="text-sm text-success-600 dark:text-success-400">
                                    {t('Pages:ScoresheetPage.PassedChecks', 'Passed Checks')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <div className="icon-[ph--x-circle] h-6 w-6 text-error-600 dark:text-error-400" />
                            <div>
                                <div className="text-2xl font-bold text-error-800 dark:text-error-200">
                                    {comparisonResult.differences.length}
                                </div>
                                <div className="text-sm text-error-600 dark:text-error-400">
                                    {t('Pages:ScoresheetPage.Differences', 'Differences')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <div className="icon-[ph--list-checks] h-6 w-6 text-primary-600 dark:text-primary-400" />
                            <div>
                                <div className="text-2xl font-bold text-primary-800 dark:text-primary-200">
                                    {comparisonResult.totalChecks}
                                </div>
                                <div className="text-sm text-primary-600 dark:text-primary-400">
                                    {t('Pages:ScoresheetPage.TotalChecks', 'Total Checks')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                    <div className="flex justify-between text-sm text-content-600 dark:text-content-400 mb-2">
                        <span>{t('Pages:ScoresheetPage.StyleCompliance', 'Style Compliance')}</span>
                        <span>{comparisonResult.passedChecks}/{comparisonResult.totalChecks}</span>
                    </div>
                    <div className="w-full bg-content-200 dark:bg-content-700 rounded-full h-3">
                        <div
                            className={`h-3 rounded-full transition-all duration-300 ${comparisonResult.compatibilityPercentage >= 80
                                ? 'bg-success-500'
                                : comparisonResult.compatibilityPercentage >= 60
                                    ? 'bg-warning-500'
                                    : 'bg-error-500'
                                }`}
                            style={{ width: `${comparisonResult.compatibilityPercentage}%` }}
                        />
                    </div>
                </div>

                {/* Section Summary */}
                {comparisonResult.sectionResults.length > 0 && (
                    <div>
                        <h3 className="text-lg font-medium text-content-900 dark:text-content-100 mb-3">
                            {t('Pages:ScoresheetPage.SectionSummary', 'Section Summary')}
                        </h3>
                        <div className="space-y-2">
                            {comparisonResult.sectionResults.map((section) => {
                                const sectionPercentage = (section.passedChecks / section.totalChecks) * 100;
                                return (
                                    <div key={section.section} className="flex items-center justify-between py-2 px-3 bg-content-50 dark:bg-content-700/50 rounded-md">
                                        <div className="flex items-center gap-3">
                                            <div className={`h-3 w-3 rounded-full ${sectionPercentage >= 80
                                                ? 'bg-success-500'
                                                : sectionPercentage >= 60
                                                    ? 'bg-warning-500'
                                                    : 'bg-error-500'
                                                }`} />
                                            <span className="font-medium text-content-800 dark:text-content-200 capitalize">
                                                {section.section}
                                            </span>
                                        </div>
                                        <div className="text-sm text-content-600 dark:text-content-400">
                                            {section.passedChecks}/{section.totalChecks} ({sectionPercentage.toFixed(0)}%)
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComparisonOverview; 